import React, { useEffect, useState } from 'react';
import moment from 'moment';
import logo from '../../../../src/assets/Images/Clanbridge-logo.png2.png';
import qrcode from '../../../../src/assets/Images/qrcode.jpg';
import axiosInstance from '../../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';

const ReceiptPrint = ({ contentRef, formData, residents, pytAdvId }) => {
  const [installment, setInstallment] = useState([]);

  const formatValue = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    }
    if (value instanceof Date || moment(value, moment.ISO_8601, true).isValid()) {
      return moment(value).format('DD-MM-YYYY');
    }
    return value || '';
  };

  const resident = residents?.find(r => r.ResidentId === formData.ResidentId);
  const residentName = resident?.Name || '';
  const residentMobile = resident?.Mobile || '';
  const residentEmail = resident?.Email || '';
  const residentAddr = resident?.CurrentAddr || resident?.PermanentAddr || 'N/A';

  const advNo = formData?.TrnRctVchrList?.length > 0
    ? formData.TrnRctVchrList
      .filter(row => row.DBFLAG !== 'D')
      .map(row => row.PytAdvNo)
      .join(', ')
    : 'N/A';

  const formatAmountInWords = (amount) => {
    if (!amount || isNaN(amount)) return 'Zero';

    const a = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen',
      'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
    ];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const numberToWords = (num) => {
      if ((num = num.toString()).length > 9) return 'Overflow';
      const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
      if (!n) return;

      let str = '';
      str += n[1] != 0 ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + ' Crore ' : '';
      str += n[2] != 0 ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + ' Lakh ' : '';
      str += n[3] != 0 ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + ' Thousand ' : '';
      str += n[4] != 0 ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + ' Hundred ' : '';
      str += n[5] != 0 ? (str != '' ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) + ' ' : '';
      return str.trim();
    };

    const rupees = Math.floor(amount);
    const paise = Math.round((amount - rupees) * 100);
    const rupeesInWords = numberToWords(rupees);
    const paiseInWords = paise > 0 ? `and ${numberToWords(paise)} Paise` : 'and Zero Paise';
    return `${rupeesInWords} ${paiseInWords} Only`;
  };

  useEffect(() => {
    fetchInstallment();
  }, [pytAdvId]);

  const fetchInstallment = async () => {
    try {
      const response = await axiosInstance.post('BookingReports/GetBookingReportsDetail', {
        BookDtFrom: "2025-02-21",
        BookUpToDt: "2025-05-09",
        PropId: "1",
        ResidentId: "",
        RoomTypeId: "",
        RoomViewId: "",
        BookSt: pytAdvId || 1,
        Flag: "PyAdvPyMonthWise",
        Id: 0
      });
      if (response.data.Status === 0) {
        setInstallment(Array.isArray(response.data.Data) ? response.data.Data : [response.data.Data]);
      } else {
        toast.info('No installments found.', { autoClose: 1000 });
      }
    } catch (error) {
      toast.error("No response from the server.", error, { autoClose: 1000 });
    }
  };

  const hasInstallmentDetails = formData?.TrnRctVchrList?.some(row => row.RecFlag === 'R' && row.DBFLAG !== 'D');

  /* ── shared style tokens ── */
  const cell = { padding: '3px 6px', border: '1px solid #ccc', fontSize: '11px' };
  const boldCell = { ...cell, fontWeight: 'bold' };
  const thStyle = { ...boldCell, backgroundColor: '#e9ecef', textAlign: 'left' };
  const tdCenter = { ...cell, textAlign: 'center' };
  const tdRight = { ...cell, textAlign: 'right' };

  return (
    <div
      ref={contentRef}
      style={{
        padding: '14px 20px 0 20px',  /* no bottom padding — footer handles spacing */
        maxWidth: '800px',
        minHeight: '100vh',
        margin: '0 auto',
        backgroundColor: '#fff',
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',      /* flex column so footer sticks to bottom */
      }}
    >
      <style>{`
        @media print {
          @page { margin: 8mm; size: A4; }
          body { margin: 0; }
        }
      `}</style>

      <ToastContainer />

      {/* ── All main content in a flex-grow wrapper ── */}
      <div style={{ flex: 1 }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', margin: 0 }}>Payment Receipt</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src={logo} alt="logo" style={{ height: 'auto', width: '80px' }} />
            <img src={qrcode} alt="QR Code" style={{ height: '50px', width: '50px' }} />
          </div>
        </div>

        {/* ── Resident & Receipt Details ── */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '10px' }}>
          <table style={{ borderCollapse: 'collapse', width: '50%' }}>
            <thead>
              <tr><th colSpan="2" style={thStyle}>Resident Details</th></tr>
            </thead>
            <tbody>
              {[
                ['Full Name', residentName || 'N/A'],
                ['Mobile', residentMobile || 'N/A'],
                ['Email', residentEmail || 'N/A'],
                ['Address', residentAddr || 'N/A'],
              ].map(([label, value]) => (
                <tr key={label}>
                  <td style={{ ...boldCell, width: '28%' }}>{label}</td>
                  <td style={cell}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <table style={{ borderCollapse: 'collapse', width: '50%' }}>
            <thead>
              <tr><th colSpan="2" style={thStyle}>Receipt Details</th></tr>
            </thead>
            <tbody>
              {[
                ['Receipt No', formData.RecNo || 'N/A'],
                ['Receipt Date', formData.RecDt ? moment(formData.RecDt).format('DD/MM/YYYY') : 'N/A'],
                ['Receipt Amt', `₹ ${formData.RcdAmt || 0}`],
                ['Advice No', advNo],
              ].map(([label, value]) => (
                <tr key={label}>
                  <td style={{ ...boldCell, width: '28%' }}>{label}</td>
                  <td style={cell}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Payment Details ── */}
        <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '4px' }}>Payment Details</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', marginBottom: '8px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              {['S/N', 'AdvNo', 'Description', 'Qty', 'Rate', 'Tax', 'Amount (Rs)'].map((h, i) => (
                <th key={h} style={{
                  ...cell,
                  fontWeight: 'bold',
                  textAlign: i === 0 || i === 3 || i === 4 || i === 5 ? 'center' : i === 6 ? 'right' : 'left',
                  border: '1px solid #ddd',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {formData.TrnRctVchrList?.filter(row => row.DBFLAG !== 'D').map((row, index) => (
              <tr key={index}>
                <td style={{ ...tdCenter, border: '1px solid #ddd' }}>{index + 1}</td>
                <td style={{ ...cell, border: '1px solid #ddd' }}>{row.PytAdvNo}</td>
                <td style={{ ...cell, border: '1px solid #ddd' }}>{row.Remark}</td>
                <td style={{ ...tdCenter, border: '1px solid #ddd' }}>1</td>
                <td style={{ ...tdCenter, border: '1px solid #ddd' }}>{formatValue(row.Amount)}</td>
                <td style={{ ...tdCenter, border: '1px solid #ddd' }}>0</td>
                <td style={{ ...tdRight, border: '1px solid #ddd' }}>{formatValue(row.Amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Installment Details ── */}
        {hasInstallmentDetails && (
          <>
            <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '4px' }}>Installment Details</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', marginBottom: '8px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  {[['S/N', '8%'], ['Student Name', '20%'], ['Installment', '20%'], ['Details', '52%']].map(([h, w]) => (
                    <th key={h} style={{ ...cell, fontWeight: 'bold', border: '1px solid #ddd', width: w }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {installment.filter(row => row != null).length > 0 ? (
                  installment.filter(row => row != null).map((row, index) => (
                    <tr key={index}>
                      <td style={{ ...tdCenter, border: '1px solid #ddd' }}>{index + 1}</td>
                      <td style={{ ...tdCenter, border: '1px solid #ddd' }}>{row?.Name || ''}</td>
                      <td style={{ ...tdCenter, border: '1px solid #ddd' }}>{row.InstallmentNo}</td>
                      <td style={{ ...cell, border: '1px solid #ddd' }}>Being Amount Received Against: {row.PytAdvNo}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ ...cell, textAlign: 'center', border: '1px solid #ddd' }}>No installments found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}

        <hr style={{ borderTop: '1px dashed #ddd', margin: '8px 0' }} />

        {/* ── Grand Total ── */}
        <div style={{ textAlign: 'right', margin: '4px 0 8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#212529' }}>
            GRAND TOTAL (Rs):&nbsp;
            <span style={{ color: '#0077b6' }}>₹ {formatValue(formData.RcdAmt || 0)}</span>
          </span>
          <p style={{ fontStyle: 'italic', fontSize: '10px', color: '#495057', marginTop: '2px', marginBottom: 0 }}>
            {formatAmountInWords(formData.RcdAmt || 0)}
          </p>
        </div>

        <hr style={{ borderTop: '1px dashed #ddd', margin: '8px 0' }} />

        {/* ── Terms & Conditions ── */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>Terms &amp; Conditions:</div>
          <ol style={{ paddingLeft: '16px', fontSize: '13px', margin: 0, lineHeight: '1.45' }}>
            {[
              'This receipt is valid only for the amount actually received and subject to realization of cheque/online transactions.',
              'Fee once paid is non-transferable and refundable only as per the booking/admission policy agreed at registration.',
              'Residents are advised to preserve this receipt for future reference and account reconciliation.',
              'Payments via cheque, bank transfer, UPI, card, or digital mode are valid only after successful bank clearance.',
              'In case of any discrepancy, the resident must notify management within 48 hours of issuance.',
              'This receipt does not confirm admission, room allotment, or security deposit adjustment unless specified in writing.',
              'Delay or default in fee payments may attract penalties, late fees, suspension of services, or cancellation of accommodation.',
              'Security deposit refund shall be processed subject to clearance of dues, damages, notice period compliance, and exit formalities.',
              'Cash payments must be verified at the time of payment; no claims shall be entertained thereafter.',
              'Management reserves the right to revise hostel rules, fee structure, facilities, or policies from time to time.',
              'Computer-generated receipts are valid without signature unless otherwise specified.',
            ].map((text, i) => (
              <li key={i} style={{ marginBottom: '2px' }}>{text}</li>
            ))}
          </ol>
        </div>

      </div>

      {/* ── Footer — pinned to bottom via marginTop: auto ── */}
      <div style={{
        marginTop: 'auto',
        borderTop: '1px solid #e0e0e0',
        padding: '8px 0 14px',
        fontSize: '10px',
        textAlign: 'center',
        color: '#666',
      }}>
        <p style={{ margin: '2px 0' }}>Thank you for choosing Clanbridge Living Pvt Ltd</p>
        <p style={{ margin: 0 }}>For any queries, please contact: sales@clanbridge.com</p>
      </div>

    </div>
  );
};

export default ReceiptPrint;